/**
 * Fullscreen photo viewer for the vendor detail Photos tab.
 * Medium variant for fast loads, arrow-key / button navigation, Esc to
 * close, and an "open full size" escape hatch (original variant in a
 * new tab).
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthImage, open_file_handler } from '@/components/auth-image';
import { photo_file_path } from '../api';
import type { VendorPhoto } from '../types';

function format_bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function PhotoLightbox({
  vendor_id,
  photos,
  index,
  on_close,
  on_navigate,
}: {
  vendor_id: string;
  photos: VendorPhoto[];
  index: number;
  on_close: () => void;
  on_navigate: (next_index: number) => void;
}) {
  const photo = photos[index];
  const has_prev = index > 0;
  const has_next = index < photos.length - 1;
  const [open_error, set_open_error] = useState<string | null>(null);

  const handle_key = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') on_close();
      if (e.key === 'ArrowLeft' && has_prev) on_navigate(index - 1);
      if (e.key === 'ArrowRight' && has_next) on_navigate(index + 1);
    },
    [on_close, on_navigate, index, has_prev, has_next],
  );

  useEffect(() => {
    window.addEventListener('keydown', handle_key);
    return () => window.removeEventListener('keydown', handle_key);
  }, [handle_key]);

  if (!photo) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/85 backdrop-blur-sm"
      data-testid="photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} of ${photos.length}: ${photo.original_filename}`}
      onClick={on_close}
    >
      {/* Top bar */}
      <div
        className="flex items-center gap-3 p-4 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">
            {photo.type} · {photo.original_filename}
          </p>
          <p className="text-xs text-white/70">
            {format_bytes(photo.size_bytes)} · uploaded{' '}
            {new Date(photo.uploaded_at).toLocaleString()} · {index + 1}/{photos.length}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/10 hover:text-white"
          onClick={open_file_handler(photo_file_path(vendor_id, photo.id, 'full'), set_open_error)}
          data-testid="lightbox-open-full"
        >
          <ExternalLink className="h-4 w-4" /> Full size
        </Button>
        {open_error && (
          <span className="text-sm text-red-300" data-testid="lightbox-open-error">
            {open_error}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/10 hover:text-white"
          onClick={on_close}
          data-testid="lightbox-close"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Image area */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center p-4">
        {has_prev && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              on_navigate(index - 1);
            }}
            className="absolute left-4 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/25"
            data-testid="lightbox-prev"
            aria-label="Previous photo"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        <div onClick={(e) => e.stopPropagation()} className="flex max-h-full max-w-full items-center justify-center">
          <AuthImage
            // Key forces a fresh mount per photo so the loading state shows
            key={photo.id}
            path={photo_file_path(vendor_id, photo.id, 'medium')}
            alt={photo.original_filename}
            className="max-h-[78vh] max-w-full rounded-md object-contain"
            data-testid="lightbox-image"
          />
        </div>
        {has_next && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              on_navigate(index + 1);
            }}
            className="absolute right-4 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/25"
            data-testid="lightbox-next"
            aria-label="Next photo"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
}
